import React, {useEffect, useRef} from "react";
import Swiper, {Autoplay} from "swiper";
import 'swiper/css';
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";

import isMobile from "ismobilejs";

//Images
import TitleBG from "../images/background-02.jpg";
import TitleBGRet from "../images/background-02@2x.jpg";
import TitleBGWebp from "../images/background-02.webp";
import TitleBGWebpRet from "../images/background-02@2x.webp";

import src01 from "../images/image2_1-01.jpg";
import src01Ret from "../images/image2_1-01@2x.jpg";
import webp01 from "../images/image2_1-01.webp";
import webp01Ret from "../images/image2_1-01@2x.webp";

import src02 from "../images/image2_2-01.jpg";
import src02Ret from "../images/image2_2-01@2x.jpg";
import webp02 from "../images/image2_2-01.webp";
import webp02Ret from "../images/image2_2-01@2x.webp";

import src03 from "../images/image2_2-02.jpg";
import src03Ret from "../images/image2_2-02@2x.jpg";
import webp03 from "../images/image2_2-02.webp";
import webp03Ret from "../images/image2_2-02@2x.webp";

import src04 from "../images/image2_3-01.jpg";
import src04Ret from "../images/image2_3-01@2x.jpg";
import webp04 from "../images/image2_3-01.webp";
import webp04Ret from "../images/image2_3-01@2x.webp";

import article02 from "../images/article-02.jpg";
import article02Ret from "../images/article-02@2x.jpg";
import article02Webp from "../images/article-02.webp";
import article02WebpRet from "../images/article-02@2x.webp";
import article02mobile from "../images/article-02-mobile.jpg";

import levelImage from "../images/level.svg";

import Content from "./Elements/Content";
import Title from "./Elements/Title";
import Block from "./Elements/Block";
import Image from "./Global/Image";

import styles from './Section02.module.scss';
import Highlight from "./Elements/Highlight";
import ImageText from "./Elements/ImageText";
import ArticleLink from "./Elements/ArticleLink";


export default function Section02() {
    const levels = useRef(null);
    const sectionTitle = `Les acteurs de la course à la voiture autonome`;

    const levelsList = [
        `La plupart des véhicules en circulation aujourd'hui sont de niveau 0, c’est-à-dire à commande manuelle. L'humain fournit la « tâche de conduite dynamique » et il n’est assisté en aucun cas par le véhicule, même s’il peut y avoir des systèmes en place pour aider le conducteur, tels que le système de freinage d'urgence. Mais il n'est pas considéré comme une automatisation, car ne « conduit » techniquement pas le véhicule.`,
        `C’est au niveau 1 qu'apparaissent les aides à la conduite avec notamment des alertes au conducteur voire action automatique (détection d'angle mort, alerte au franchissement de file, alerte de risque de collision, freinage d'urgence automatique, park assist, etc.). Le conducteur doit rester attentif à la route et doit garder les mains sur le volant.`,
        `Le niveau 2 correspond à une automatisation partielle. Le système automatique est capable de contrôler le véhicule, mais le conducteur doit constamment surveiller la conduite et doit être prêt à intervenir à tout moment pour reprendre le contrôle si le système ne réagit pas comme prévu. Un véhicule autonome de deuxième niveau est doté d’au moins deux fonctionnalités automatisées pouvant gérer des tâches basiques (parking, maintient dans la voie…).`,
        `A partir du niveau 3, il est véritablement possible de parler de conduite autonome (direction, accélération, freinage), mais celle-ci reste limitée à certaines conditions (par exemple sur autoroute). Le conducteur doit être en permanence en mesure de reprendre le contrôle et conduire dans les zones où l’automatisation ne peut s’opérer.`,
        `Le niveau 4 correspond à un haut niveau d’automatisation. Un véhicule de quatrième niveau peut effectuer la quasi-totalité des trajets en complète autonomie, sans l’assistance du conducteur. L’autonomie est complète sous certaines conditions météorologiques et dans certaines zones géographiques. Le passager peut toujours reprendre le volant, lorsque les limites sont atteintes dans des cas très particuliers.`,
        `Encore hors de portée, le niveau 5 désigne l’autonomie complète en toutes conditions. Le véhicule est totalement autonome et en charge dans l’ensemble des situations et l’intervention humaine est inutile. Le passager peut profiter du trajet, le volant est optionnel…`
    ];

    const levelTable = [
        {
            title: "Conducteur seul",
            eyes: "opened",
            hands: 2,
            icon: "wheel",
            assist: null,
            description: `Aucune aide`
        },
        {
            title: "Accompagné",
            eyes: "opened",
            hands: 2,
            icon: "wheel",
            assist: "direction",
            description: `Aides primaires : AFU, régulateur de vitesse adaptatif …`
        },
        {
            title: "Assisté",
            eyes: "opened",
            hands: 1,
            icon: "wheel",
            assist: "park",
            description: `Aides intermédiaires : Alerte franchissement de ligne, park assist …`
        },
        {
            title: "Guidé",
            eyes: "opened",
            hands: 0,
            icon: "wheel",
            assist: "overtake",
            description: `Aides avancées : Dépassements dynamiques`
        },
        {
            title: "Passif",
            eyes: "closed",
            hands: 0,
            icon: "wheel",
            assist: null,
            description: `Conduite autonome complète à la demande`
        },
        {
            title: "100% Autonome",
            eyes: null,
            hands: null,
            icon: "self-driving",
            assist: null,
            description: `Conduite exclusivement autonome`
        }
    ];

    useEffect(() => {
        if (isMobile().phone) {
            new Swiper('.swiper', {
                modules: [Autoplay],
                slidesPerView: 1,
                spaceBetween: 3,
                autoplay: {
                    delay: 1000
                }
            });
        }
    }, []);

    useEffect(() => {
        gsap.registerPlugin(ScrollTrigger);

        gsap.timeline({
            scrollTrigger: {
                trigger: levels.current,
                scrub: 0.2,
                start: 'top top',
                end: '+=10000',
            }
        })
            .to(levels.current.querySelectorAll(`.${styles.LevelNumber} figure`), {
                rotation: 360 * 5,
                duration: 1, ease: 'none',
            });
    }, []);

    return (
        <Content classname={'navigation--1'}>
            <Title title={sectionTitle} numero={2}
                   background={{src: TitleBG, srcRet: TitleBGRet, webp: TitleBGWebp, webpRet: TitleBGWebpRet}}/>

            <Block title={`Les niveaux d'autonomie`} number={2.1} sectionTitle={sectionTitle} container={true}
                   classname={'navigation--2'}>
                <p className={'size-5 ml-4'}>
                    Les niveaux d'autonomie d'un véhicule déterminent les capacités d'un véhicule à circuler en se
                    substituant plus ou moins au conducteur, et les conditions de cette autonomie. Ils sont régis par
                    deux référentiels : celui américain de la NHTSA (National highway Traffic Safety Administration) qui
                    est composé de 5 niveaux allant de «&nbsp;0&nbsp;» à «&nbsp;4) ; et celui international défini par
                    l’OICA (Organisation Internationale des Constructeurs Automobiles) et la SAE International (Society
                    of Automotive Engineers) qui comporte 6 niveaux allant de «&nbsp;0&nbsp;» à «&nbsp;5&nbsp;».
                </p>

                <Image classname={[styles.Image, 'size-6'].join(' ')}
                       src={src01} srcRet={src01Ret} webp={webp01} webpRet={webp01Ret}
                       radius={true}/>

                <div className={styles.Levels} ref={levels}>
                    <ul>
                        {
                            levelsList.map((item, index) =>
                                <li key={index}>
                                    <span className={styles.LevelNumber}>
                                        {index}

                                        <Image src={levelImage}/>
                                    </span>

                                    <p>
                                        {item}
                                    </p>
                                </li>
                            )
                        }
                    </ul>
                </div>

                <Highlight title={'Les six niveaux d’autonomie'} source={'SAE International'} color={'blue'}
                           font={'black'} classname={styles.LevelHighlight}>
                    <div className="swiper">
                        <div className={'swiper-wrapper'}>
                            {
                                levelTable.map((item, index) => {
                                    const eyes = item.eyes ? require(`../images/eyes-${item.eyes}.svg`) : null;
                                    const hands = item.hands !== null ? require(`../images/hands-${item.hands}.svg`) : null;
                                    const icon = require(`../images/${item.icon}.svg`);
                                    const assist = item.assist ? require(`../images/assist-${item.assist}.svg`) : null;

                                    return (
                                        <div key={index} className={'swiper-slide'}>
                                            <span className={styles.LevelHighlightNumber}>
                                                {index}
                                            </span>

                                            <span>
                                                {item.title}
                                            </span>

                                            <div className={styles.LevelHighlightIllusration}>
                                                {
                                                    eyes ? <Image src={eyes}/> : null
                                                }

                                                <div className={styles.LevelHighlightWheel}>
                                                    {
                                                        hands ? <Image src={hands}
                                                                       classname={item.hands === 2 ? styles.TwoHands : (item.hands === 0 ? 'no--hands' : null)} />
                                                                       : null
                                                    }

                                                    <Image src={icon}/>
                                                </div>

                                                {
                                                    assist ? <Image src={assist}/> : null
                                                }
                                            </div>

                                            <span className={styles.LevelHighlightDescription}>
                                            {item.description}
                                        </span>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    </div>
                </Highlight>
            </Block>

            <Block title={`Les grands constructeurs`} number={2.2} sectionTitle={sectionTitle}
                   classname={'navigation--2'}>
                <div className="container">
                    <ImageText image={{src: src02, srcRet: src02Ret, webp: webp02, webpRet: webp02Ret}} radius={true}
                               classname={'with--margin rtl large'}>
                        <p>Les grands constructeurs ont des projets de voiture autonome bien avancés. Les ingénieurs
                            travaillent d’arrache-pied aux quatre coins de la planète. Leur approche scientifique passe
                            outre pour le moment les barrières régionales… Restera ensuite à convaincre les populations
                            en fonction de leurs différences culturelles et du contexte local. Tout le monde parle des
                            constructeurs automobiles américains, avec Tesla, General Motors, ou encore Ford, mais les
                            acteurs asiatiques sont aussi en pointe, comme au Japon avec Toyota ou Honda qui est le seul
                            à avoir sorti un modèle à commercialisation de niveau 3. «&nbsp;La ‘Honda Legend’ est
                            capable de contrôler la voiture à la place du conducteur dans certaines situations comme un
                            embouteillage ou sur une autoroute. Honda a simulé 10 millions de scénarii de situations de
                            conduite réelle et a effectué environ 1,3 million de kilomètres de tests&nbsp;», rappelle
                            Antoine Fraysse-Soulier, analyste chez eToro. </p>
                    </ImageText>
                </div>

                <ImageText image={{src: src03, srcRet: src03Ret, webp: webp03, webpRet: webp03Ret}} radius={true}
                           classname={'banner'}>
                    <p>La Chine occupe aussi le terrain avec Geely, qui a signé un partenariat avec Baidu pour mettre au
                        point des véhicules autonomes. Dans cette co-entreprise, Baidu va fournir les technologies pour
                        la conduite sans chauffeur et Geely apportera son expertise dans la conception et la
                        fabrication. La Chine peut aussi compter sur Nio ou encore Didi, avec une flotte de robots taxis
                        qui s’est introduite en bourse récemment. Le groupe souhaitait initialement lancer plus de 3.000
                        voitures dans des villes chinoises mais va avoir du mal à arriver à ses fins en raison des
                        freins mis en place par Pékin. </p>
                </ImageText>

                <div className="container">
                    <p className={'size-5 ml-5'}>
                        En Europe, les Allemands sont très actifs avec BMW et Daimler, la maison-mère de Mercedes,
                        tandis que le français Renault et Stellantis ne sont pas en reste... Du côté des équipementiers,
                        les acteurs se comptent par centaine dans l’intelligence artificielle avec des pays très en
                        pointe comme Israël où les fabricants de radars, lasers, cartes graphiques et système de
                        navigation s’intéressent de près à ce marché. Aux Etats-Unis, Nvidia est bien positionné, tout
                        comme Infineon en Allemagne et Valeo en France.
                    </p>
                </div>
            </Block>

            <Block title={`De nouveaux acteurs`} number={2.3} sectionTitle={sectionTitle} container={true}
                   classname={'navigation--2'}>
                <ImageText image={{src: src04, srcRet: src04Ret, webp: webp04, webpRet: webp04Ret}} radius={true}
                           classname={'with--margin rtl large'}>
                    <p>Toutes les sociétés liées au «&nbsp;cloud&nbsp;» sont également incontournables : Microsoft, via
                        sa plateforme Azure, a conclu en début d’année un partenariat avec Cruise, la filiale de
                        voitures autonomes de General Motors. Apple a de son côté fait récemment des annonces très
                        prometteuses : le groupe planche via son projet «&nbsp;Titan&nbsp;» sur la fameuse Apple Car qui
                        pourrait être lancée en 2025. L’objectif du groupe à la pomme est de sortir une voiture
                        totalement autonome, donc sans chauffeur, sans pédale ni volant, en se démarquant sur le design
                        face à ses concurrents. La firme de Cupertino a d’ailleurs récemment accéléré et aurait effectué
                        des avancées décisives dans ce projet, en mettant notamment au point l'essentiel de son
                        processeur. En novembre dernier, Apple a également annoncé avoir recruté Christopher
                        «&nbsp;CJ&nbsp;» Moore, l’ancien responsable du programme Autopilot chez Tesla, pour se charger
                        du développement de sa voiture autonome. Mais les défis restent nombreux, d’autant plus que
                        l’industrie automobile souffre depuis déjà plusieurs mois d’une pénurie de semi-conducteurs.</p>
                </ImageText>

                <ArticleLink title={`Apple : une révolution titanesque ? `}
                             href={'https://www.boursier.com/actualites/economie/apple-une-revolution-titanesque-47478.html'}
                             image={{
                                 src: article02,
                                 srcRet: article02Ret,
                                 webp: article02Webp,
                                 webpRet: article02WebpRet
                             }} mobile={article02mobile} classname={'large'}/>

                <p className={'size-6 ml-4'}>Parmi les nouveaux entrants, on peut également nommer le petit Français
                    Navya. Son premier véhicule électrique et autonome de série disponible à la vente, la «&nbsp;Navya
                    Arma&nbsp;» rebaptisé «&nbsp;Autonom Shuttle&nbsp;» en novembre 2017, est présenté en première
                    mondiale à l’ITS World Congress de Bordeaux en octobre 2015, où il a circulé sur route ouverte. En
                    avril 2021, l’entreprise française spécialisée dans la conception et la construction de véhicules
                    autonomes, électriques et robotisés s'est rapprochée de la société israélienne, REE Automotive, pour
                    concevoir un véhicule autonome de niveau 4 de nouvelle génération. Ils doivent proposer le résultat
                    de leur travail à des opérateurs de mobilité…</p>
            </Block>
        </Content>
    )
}
